import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Download, FileText } from "lucide-react";
import { Fragment, useState } from "react";
import { downloadUpload } from "../api";
import { wmErrorMessage } from "../api/wm-client";
import type {
  ComparisonValueType,
  ComparisonVersion,
  ComparisonView,
} from "../dtos";

interface LabeledValue {
  key: string;
  label: string;
  format: "text" | "money" | "number" | "percent";
}

const labeledValue = (
  key: string,
  label: string,
  format: LabeledValue["format"],
): LabeledValue => ({ key, label, format });

const INPUT_ROWS: LabeledValue[] = [
  labeledValue("cashPrice", "Cash Price", "money"),
  labeledValue("downPayment", "Down Payment", "money"),
  labeledValue("tenorYears", "Tenor (years)", "number"),
  labeledValue("frequency", "Installment Frequency", "text"),
  labeledValue(
    "leadingMonths",
    "Monthly Installments Before Quarterly",
    "number",
  ),
  labeledValue("propertyUsage", "Property Usage", "text"),
  labeledValue("loanAmount", "Loan Amount", "money"),
  labeledValue("financeProduct", "Finance Product", "text"),
];

const SUMMARY_ROWS: LabeledValue[] = [
  labeledValue("installment", "Installment", "money"),
  labeledValue("contractTotal", "Contract Total", "money"),
  labeledValue("totalInstallments", "Total Installments", "money"),
  labeledValue("paymentCount", "Number of Installments", "number"),
  labeledValue("requiredIncome", "Required Income", "money"),
  labeledValue("flatRate", "Flat Rate", "percent"),
  labeledValue("ltv", "Loan-to-value (LTV)", "percent"),
  labeledValue("gmv", "GMV", "money"),
  labeledValue("npv", "NPV", "money"),
];

const SCHEDULE_COLUMNS: LabeledValue[] = [
  labeledValue("period", "#", "number"),
  labeledValue("monthOffset", "Month", "number"),
  labeledValue("dueDate", "Due date", "text"),
  labeledValue("installment", "Installment", "money"),
  labeledValue("principal", "Principal", "money"),
  labeledValue("interest", "Interest", "money"),
  labeledValue("balance", "Balance", "money"),
  labeledValue("outstanding", "Outstanding", "money"),
];

const formatDisplayValue = (
  value: unknown,
  format: LabeledValue["format"],
): string => {
  if (value === null || value === undefined || value === "") return "—";
  const number = typeof value === "number" ? value : Number(value);
  if (format === "money") {
    if (!Number.isFinite(number)) return "—";
    return `EGP ${number.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;
  }
  if (format === "number") {
    if (!Number.isFinite(number)) return "—";
    return number.toLocaleString("en-US", { maximumFractionDigits: 2 });
  }
  if (format === "percent") {
    if (!Number.isFinite(number)) return "—";
    return `${(Math.abs(number) <= 1 ? number * 100 : number).toFixed(1)}%`;
  }
  return String(value);
};

const formatValue = (
  value: string | number | null,
  type: ComparisonValueType,
  key: string,
): string => {
  if (type === "number" && ["flatRate", "ltv", "paymentPlanFile.flatRate", "paymentPlanFile.ltv"].includes(key)) return formatDisplayValue(value, "percent");
  return formatDisplayValue(value, type);
};

const provenance = (version: ComparisonVersion): string => {
  const savedVersion =
    version.submissionVersion > 0
      ? ` · version ${version.submissionVersion}`
      : "";
  return `${version.stepName}${savedVersion} · ${version.actorName} · ${new Date(
    version.completedAt,
  ).toLocaleString()}`;
};

const ValueList = ({
  title,
  rows,
  values,
}: {
  title: string;
  rows: LabeledValue[];
  values: Record<string, unknown>;
}) => {
  const visible = rows.filter(
    (row) =>
      values[row.key] !== undefined &&
      values[row.key] !== null &&
      values[row.key] !== "",
  );
  if (!visible.length) return null;
  return (
    <Box component="section" aria-label={title} sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
        {title}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(150px, 1fr) minmax(100px, auto)",
          gap: 0.75,
        }}
      >
        {visible.map((row) => (
          <Fragment key={row.key}>
            <Typography variant="body2" color="text.secondary">
              {row.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 650, textAlign: "right" }}
            >
              {formatDisplayValue(values[row.key], row.format)}
            </Typography>
          </Fragment>
        ))}
      </Box>
    </Box>
  );
};

export const SavedPlanContent = ({
  label,
  version,
}: {
  label: string;
  version: ComparisonVersion | null;
}) => {
  const [downloadError, setDownloadError] = useState("");
  if (!version?.plan) {
    return (
      <Paper
        component="section"
        variant="outlined"
        aria-label={`${label} full saved plan`}
        sx={{ p: 2, minWidth: 0 }}
      >
        <Typography variant="h6" sx={{ fontSize: "1rem" }}>
          {label}
        </Typography>
        {version && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", my: 0.75 }}
          >
            {provenance(version)}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          No reliable saved full plan is available.
        </Typography>
      </Paper>
    );
  }

  const plan = version.plan;
  const special =
    String(plan.inputs.frequency ?? "").toLowerCase() === "special";
  const inputs = special
    ? INPUT_ROWS
    : INPUT_ROWS.filter((row) => row.key !== "leadingMonths");
  const download = (uid: string, fileName: string): void => {
    setDownloadError("");
    downloadUpload(uid, fileName).catch((caught) =>
      setDownloadError(wmErrorMessage(caught)),
    );
  };

  return (
    <Paper
      component="section"
      variant="outlined"
      aria-label={`${label} full saved plan`}
      sx={{ p: 2, minWidth: 0 }}
    >
      <Typography variant="h6" sx={{ fontSize: "1rem", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {provenance(version)}
      </Typography>
      {downloadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {downloadError}
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { mobile: "1fr", tablet: "1fr 1fr" },
          gap: 2,
        }}
      >
        <ValueList title="Business inputs" rows={inputs} values={plan.inputs} />
        <ValueList
          title="Calculated figures"
          rows={SUMMARY_ROWS}
          values={plan.summary}
        />
      </Box>
      {plan.files.length > 0 && (
        <Box
          component="section"
          aria-label="Saved files"
          sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}
        >
          {plan.files.map((file) => (
            <Button
              key={file.uid}
              variant="outlined"
              startIcon={<Download size={15} />}
              onClick={() => download(file.uid, file.fileName)}
            >
              Download {file.fileName}
            </Button>
          ))}
        </Box>
      )}
      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
        Saved installment schedule
      </Typography>
      {plan.schedule.length ? (
        <TableContainer
          sx={{
            maxHeight: "44vh",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Table
            stickyHeader
            size="small"
            aria-label={`${label} saved installment schedule`}
          >
            <TableHead>
              <TableRow>
                {SCHEDULE_COLUMNS.map((column) => (
                  <TableCell key={column.key}>{column.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {plan.schedule.map((row, index) => (
                <TableRow
                  key={`${String(row.dueDate ?? "")}-${String(
                    row.period ?? index,
                  )}`}
                  sx={{
                    contentVisibility: "auto",
                    containIntrinsicSize: "36px",
                  }}
                >
                  {SCHEDULE_COLUMNS.map((column) => (
                    <TableCell key={column.key}>
                      {formatDisplayValue(row[column.key], column.format)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No saved schedule is available.
        </Typography>
      )}
    </Paper>
  );
};

export const ComparisonPlansContent = ({
  comparison,
}: {
  comparison: ComparisonView;
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        mobile: "1fr",
        desktop: "minmax(0, 1fr) minmax(0, 1fr)",
      },
      gap: 2,
    }}
  >
    <SavedPlanContent label={comparison.oldLabel} version={comparison.old} />
    <SavedPlanContent label={comparison.newLabel} version={comparison.new} />
  </Box>
);

const SavedPlanDialog = ({
  open,
  label,
  version,
  onClose,
}: {
  open: boolean;
  label: string;
  version: ComparisonVersion;
  onClose: () => void;
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="desktop" fullWidth>
    <DialogTitle>{label} saved plan</DialogTitle>
    <DialogContent dividers>
      <SavedPlanContent label={label} version={version} />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

const SideBySidePlansDialog = ({
  comparison,
  open,
  onClose,
}: {
  comparison: ComparisonView;
  open: boolean;
  onClose: () => void;
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="desktop" fullWidth>
    <DialogTitle>{comparison.label} saved plans</DialogTitle>
    <DialogContent dividers>
      <ComparisonPlansContent comparison={comparison} />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

const stateCopy = (
  comparison: ComparisonView,
): {
  label: string;
  color: "info" | "success" | "warning" | "default";
} => {
  if (comparison.state === "changed") {
    return { label: "Changed", color: "warning" };
  }
  if (comparison.state === "unchanged") {
    return { label: "Unchanged", color: "success" };
  }
  if (comparison.state === "awaiting_new") {
    return { label: "Awaiting updated plan", color: "info" };
  }
  return { label: "History unavailable", color: "default" };
};

const ComparisonCard = ({ comparison }: { comparison: ComparisonView }) => {
  const [showAll, setShowAll] = useState(false);
  const [dialog, setDialog] = useState<{
    label: string;
    version: ComparisonVersion;
  } | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const state = stateCopy(comparison);
  const visibleRows = showAll
    ? comparison.rows
    : comparison.rows.filter((row) => row.changed);
  const hasHiddenRows = comparison.rows.some((row) => !row.changed);

  return (
    <Paper
      component="article"
      variant="outlined"
      sx={{
        boxSizing: "border-box",
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
        p: { mobile: 1.5, tablet: 2 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexWrap: "wrap",
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: "1rem",
            fontWeight: 750,
            flex: "1 1 12rem",
            minWidth: 0,
            overflowWrap: "anywhere",
          }}
        >
          {comparison.label}
        </Typography>
        <Chip size="small" label={state.label} color={state.color} />
        {comparison.scheduleChanged && (
          <Chip
            size="small"
            variant="outlined"
            color="warning"
            label="Saved schedule changed"
          />
        )}
      </Box>

      {comparison.message && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {comparison.message}
        </Typography>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { mobile: "1fr", tablet: "1fr 1fr" },
          gap: 1.5,
          mb: 1.5,
        }}
      >
        {(
          [
            { label: comparison.oldLabel, version: comparison.old },
            { label: comparison.newLabel, version: comparison.new },
          ] as const
        ).map((side) => (
          <Box
            key={side.label}
            sx={{ p: 1.25, bgcolor: "action.hover", borderRadius: 1 }}
          >
            <Typography variant="subtitle2">{side.label}</Typography>
            {side.version ? (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", my: 0.5 }}
                >
                  {provenance(side.version)}
                </Typography>
                {side.version.plan && (
                  <Button
                    size="small"
                    startIcon={<FileText size={14} />}
                    onClick={() =>
                      setDialog({
                        label: side.label,
                        version: side.version as ComparisonVersion,
                      })
                    }
                  >
                    View {side.label} saved plan
                  </Button>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No reliable completed version available.
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      {(comparison.old?.plan || comparison.new?.plan) && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileText size={14} />}
          sx={{ mb: 1.5 }}
          onClick={() => setCompareOpen(true)}
        >
          Compare saved plans
        </Button>
      )}

      {visibleRows.length > 0 && (
        <TableContainer>
          <Table size="small" aria-label={`${comparison.label} fields`}>
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>{comparison.oldLabel}</TableCell>
                <TableCell>{comparison.newLabel}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row) => (
                <TableRow
                  key={row.key}
                  sx={row.changed ? { bgcolor: "warning.light" } : undefined}
                >
                  <TableCell sx={{ fontWeight: row.changed ? 700 : 400 }}>
                    {row.label}
                  </TableCell>
                  <TableCell>
                    {row.oldValueAvailable === false
                      ? "Not recorded"
                      : formatValue(row.oldValue, row.type, row.key)}
                  </TableCell>
                  <TableCell>
                    {row.newValueAvailable === false
                      ? "Not recorded"
                      : formatValue(row.newValue, row.type, row.key)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {hasHiddenRows && (
        <Button
          size="small"
          sx={{ mt: 1 }}
          onClick={() => setShowAll((value) => !value)}
        >
          {showAll ? "Show changed fields" : "Show all fields"}
        </Button>
      )}
      {dialog && (
        <SavedPlanDialog
          open
          label={dialog.label}
          version={dialog.version}
          onClose={() => setDialog(null)}
        />
      )}
      <SideBySidePlansDialog
        comparison={comparison}
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
      />
    </Paper>
  );
};

export const ComparisonPanel = ({
  comparisons,
}: {
  comparisons: ComparisonView[];
}) => {
  const changedComparisons = comparisons.filter(
    (comparison) => comparison.state === "changed",
  );
  if (!changedComparisons.length) return null;
  return (
    <Box
      component="section"
      aria-label="Saved plan comparisons"
      sx={{ display: "grid", gap: 1.5, mb: 2, minWidth: 0, width: "100%" }}
    >
      {changedComparisons.map((comparison) => (
        <ComparisonCard key={comparison.id} comparison={comparison} />
      ))}
    </Box>
  );
};
